import { useState } from 'react';
import { Link, NavLink, Outlet } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { useAuth } from '../../lib/auth';

const links = [['/models', 'Models'], ['/how-it-works', 'How it works'], ['/about', 'About'], ['/custom-models', 'Custom modelling']];

export function Brand({ className = '', testId = 'brand-link' }) {
  return <Link to="/" className={`shrink-0 whitespace-nowrap font-heading text-lg font-bold text-foreground ${className}`} data-testid={testId}>numbercrunch<span className="text-[hsl(var(--teal))]">.ai</span></Link>;
}

export function Nav() {
  const { session } = useAuth();
  const [open, setOpen] = useState(false);
  const cls = ({ isActive }) => `text-sm font-medium transition-colors hover:text-foreground ${isActive ? 'text-foreground' : 'text-muted-foreground'}`;
  return <header className="sticky top-0 z-40 border-b border-border bg-background/90 backdrop-blur" data-testid="public-nav">
    <div className="container-x flex h-16 items-center justify-between">
      <Brand />
      <nav className="hidden items-center gap-7 md:flex" aria-label="Main">
        {links.map(([to, label]) => <NavLink key={to} to={to} className={cls} data-testid={`nav-${label.toLowerCase().replace(/ /g, '-')}`}>{label}</NavLink>)}
      </nav>
      <div className="hidden items-center gap-3 md:flex">
        {session ? <Link to="/app" className="btn-ghost" data-testid="nav-my-models">My models</Link> : <Link to="/login" className="btn-ghost" data-testid="nav-login">Log in</Link>}
        <Link to={session ? '/app/models/new' : '/signup?next=%2Fapp%2Fmodels%2Fnew'} className="btn-primary h-10" data-testid="nav-build-model">Build a free model</Link>
      </div>
      <button className="btn-ghost md:hidden" aria-expanded={open} aria-label="Toggle menu" onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle">{open ? <X size={20} /> : <Menu size={20} />}</button>
    </div>
    {open && <div className="border-t border-border bg-background md:hidden" data-testid="nav-mobile-menu">
      <div className="container-x flex flex-col gap-1 py-3">
        {links.map(([to, label]) => <NavLink key={to} to={to} onClick={() => setOpen(false)} className="rounded-md px-2 py-2 text-sm font-medium hover:bg-secondary" data-testid={`mobile-nav-${to.slice(1)}`}>{label}</NavLink>)}
        <Link to={session ? '/app' : '/login'} onClick={() => setOpen(false)} className="rounded-md px-2 py-2 text-sm font-medium hover:bg-secondary" data-testid="mobile-nav-account">{session ? 'My models' : 'Log in'}</Link>
        <Link to={session ? '/app/models/new' : '/signup?next=%2Fapp%2Fmodels%2Fnew'} onClick={() => setOpen(false)} className="btn-primary mt-2" data-testid="mobile-nav-create">Build a free model</Link>
      </div>
    </div>}
  </header>;
}

export function Footer() {
  return <footer className="mt-24 border-t border-border bg-card" data-testid="public-footer">
    <div className="container-x grid gap-10 py-14 md:grid-cols-4">
      <div className="md:col-span-2">
        <Brand testId="footer-brand-link" />
        <p className="mt-3 max-w-sm text-sm text-muted-foreground">Free, formula-linked financial models for real businesses, built by a Chartered Accountant. No credit card, no locked formulas.</p>
        <p className="mt-4 text-xs text-muted-foreground"><Link to="/custom-models" className="underline" data-testid="footer-contact">Contact Swapnil</Link> · <Link to="/report-issue" className="underline" data-testid="footer-report-issue">Report a model issue</Link></p>
      </div>
      <div>
        <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Product</p>
        <ul className="space-y-2 text-sm">
          {[['/models', 'Model catalogue'], ['/how-it-works', 'How it works'], ['/methodology', 'Methodology'], ['/help', 'Help and glossary'], ['/custom-models', 'Custom modelling']].map(([to, l]) => <li key={to}><Link to={to} className="text-muted-foreground hover:text-foreground" data-testid={`footer-${to.slice(1)}`}>{l}</Link></li>)}
        </ul>
      </div>
      <div>
        <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Company</p>
        <ul className="space-y-2 text-sm">
          {[['/about', 'About Swapnil Jain'], ['/privacy', 'Privacy'], ['/terms', 'Terms']].map(([to, l]) => <li key={to}><Link to={to} className="text-muted-foreground hover:text-foreground" data-testid={`footer-${to.slice(1)}`}>{l}</Link></li>)}
        </ul>
      </div>
    </div>
    <div className="border-t border-border"><div className="container-x py-5 text-xs text-muted-foreground">© {new Date().getFullYear()} numbercrunch.ai. Models are planning tools, not financial, tax or investment advice.</div></div>
  </footer>;
}

export default function PublicLayout() {
  return <>
    <a href="#main" className="skip-link" data-testid="skip-content">Skip to content</a>
    <Nav />
    <main id="main"><Outlet /></main>
    <Footer />
  </>;
}
